import { initializeApp, deleteApp, setLogLevel, onLog } from 'firebase/app'
import { getAuth, signInWithCustomToken } from 'firebase/auth'
import {
  getDatabase,
  ref,
  type Database,
  type DatabaseReference,
} from 'firebase/database'

import {
  registerFatalDisconnect,
  setFatalError,
} from '../FatalError/FatalError'
import { getWebinarConnectInfo } from '../global/blocs/useConnectInfo'
import { getLiveId, getPresenterToken, getRole } from '../global/blocs/roomInfo'
import { logBreadcrumb, logMessage } from '../Logging/log'
import { DeferredSystem } from '../global/utils/DeferredSystem'

type FirebaseRoomIdentification = {
  roomId: string
  participantId: number
  role: string
}

type FirebaseConnectionConfig = FirebaseRoomIdentification & {
  database: Database
}

class FirebaseConnection {
  /** The room identifier */
  roomId: string

  /** The participant identifier */
  participantId: number

  /** The role of the participant */
  role: string

  /** A unique identifier generated for each firebase session */
  sessionId: string

  /** */
  database: Database

  room: DatabaseReference

  constructor({
    roomId,
    participantId,
    role,
    database,
  }: FirebaseConnectionConfig) {
    this.roomId = roomId
    this.participantId = participantId
    this.role = role
    this.database = database
    this.room = ref(this.database, roomId)

    // Generate a new session token
    this.sessionId = crypto.randomUUID()
  }

  // oxlint-disable-next-line class-methods-use-this
  logError(type: string, message: string, originalError: unknown) {
    logMessage(message, {
      level: 'error',
      extra: {
        type,
        reason: originalError,
      },
    })
  }
}

const initializeFirebase = async () => {
  if (getRole() === 'audience') return undefined

  const webinarInfo = await getWebinarConnectInfo()

  const {
    webrtc_data_key: roomId,
    live_speaker_id: participantId,
    webrtc_role: role,
    firebase_api_key: apiKey,
    firebase_auth_domain: authDomain,
    firebase_database_url: databaseURL,
    firebase_storage_bucket: storageBucket,
    webrtc_data_authenticator: token,
  } = webinarInfo

  const firebaseInstanceName = `${getLiveId()}|${getRole()}|${getPresenterToken()}|v2`

  try {
    onLog(
      ({ type, level, message, args }) => {
        if (level === 'error') {
          logMessage(`Firebase — ${message}`, {
            level,
            extra: {
              type,
              args,
            },
          })
        } else {
          logBreadcrumb(
            {
              timestamp: Date.now() / 1000,
              category: 'firebase',
              message: `Firebase — ${message}`,
              type,
              level: (
                {
                  debug: 'debug',
                  verbose: 'debug',
                  info: 'info',
                  warn: 'warning',
                  error: 'error',
                  silent: 'debug',
                } as const
              )[level],
            },
            { args }
          )
        }
      },
      { level: 'info' }
    )

    setLogLevel('info')

    const app = initializeApp(
      { apiKey, authDomain, databaseURL, storageBucket },
      { name: firebaseInstanceName, automaticDataCollectionEnabled: false }
    )

    registerFatalDisconnect(() => {
      deleteApp(app).catch(() => {})
    })

    await signInWithCustomToken(getAuth(app), token)

    const database = getDatabase(app)

    return new FirebaseConnection({ roomId, participantId, role, database })
  } catch (err) {
    setFatalError('FIREBASE_CONNECTION_ERROR', { reload: 'ask', reason: err })
    return undefined
  }
}

const firebaseConnection = new DeferredSystem<FirebaseConnection>(
  initializeFirebase
)

const firebaseWSConnectionFix = () => {
  try {
    localStorage.removeItem('firebase:previous_websocket_failure')
  } catch (e) {
    logMessage(
      "Firebase — Couldn't clean up localstorage websocket failure flag",
      { level: 'warning', extra: { cause: e } }
    )
  }
}

const runWhenReady = (cb: (system: FirebaseConnection) => void) => {
  firebaseWSConnectionFix()
  return firebaseConnection.runWhenReady(cb)
}

const get = () => {
  firebaseWSConnectionFix()
  return firebaseConnection.get()
}

export { runWhenReady, get, type FirebaseConnection }
